import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faCalendarAlt,
  faCheck,
  faEye,
  faFilter,
  faPlus,
  faRefresh,
  faSearch,
  faTimes,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  addProviderPaymentHoliday,
  deleteProviderPaymentHoliday,
  getProviderPaymentHolidayById,
  getProviderPaymentHolidayList,
} from "../../services/providerPaymentBatchAddHolidayApi";

const DEFAULT_FILTERS = {
  search: "",
  addedBy: "",
  recurrent: "",
  dateFrom: "",
  dateTo: "",
};

const PER_PAGE_OPTIONS = [10, 20, 30, 50, 100];

const BatchAddHolidayListPage = () => {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total: 0 });

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingHoliday, setViewingHoliday] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [addForm, setAddForm] = useState({
    holidayName: "",
    holidayDate: "",
    recurrent: false,
  });

  const fetchHolidayList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: perPage,
        search: appliedFilters.search,
        added_by: appliedFilters.addedBy,
        recurrent: appliedFilters.recurrent,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        sort_by: "date",
        sort_dir: "desc",
      };

      const response = await getProviderPaymentHolidayList(params);
      setHolidays(response.data?.data || []);
      setPagination(response.data?.pagination || { current_page: 1, total_pages: 1, total: 0 });
    } catch (error) {
      toast.error("Failed to fetch holiday list.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedFilters]);

  useEffect(() => {
    fetchHolidayList();
  }, [fetchHolidayList]);

  const hasActiveFilters = useMemo(() => {
    return (
      appliedFilters.search ||
      appliedFilters.addedBy ||
      appliedFilters.recurrent ||
      appliedFilters.dateFrom ||
      appliedFilters.dateTo
    );
  }, [appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const handleOpenAddModal = () => {
    setAddForm({ holidayName: "", holidayDate: "", recurrent: false });
    setShowAddModal(true);
  };

  const handleAddHoliday = async (event) => {
    event.preventDefault();

    if (!addForm.holidayDate) {
      toast.error("Holiday date is required.");
      return;
    }

    setAdding(true);
    try {
      await addProviderPaymentHoliday({
        holiday_name: addForm.holidayName,
        holiday_date: addForm.holidayDate,
        recurrent: addForm.recurrent,
      });

      toast.success("Holiday added successfully.");
      setShowAddModal(false);
      if (page === 1) {
        await fetchHolidayList();
      } else {
        setPage(1);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add holiday.";
      toast.error(typeof message === "string" ? message : "Failed to add holiday.");
      console.error(error);
    } finally {
      setAdding(false);
    }
  };

  const handleViewHoliday = async (holidayId) => {
    try {
      const response = await getProviderPaymentHolidayById(holidayId);
      setViewingHoliday(response.data?.data || null);
      setShowViewModal(true);
    } catch (error) {
      toast.error("Failed to load holiday details.");
      console.error(error);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    const confirmed = window.confirm("Delete this holiday?");
    if (!confirmed) return;

    setDeletingId(holidayId);
    try {
      await deleteProviderPaymentHoliday(holidayId);
      toast.success("Holiday deleted successfully.");

      if (holidays.length === 1 && page > 1) {
        setPage((prev) => Math.max(1, prev - 1));
      } else {
        await fetchHolidayList();
      }
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete holiday.";
      toast.error(typeof message === "string" ? message : "Failed to delete holiday.");
      console.error(error);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDateLong = (value) => {
    if (!value) return "-";
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;

    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatRecurrent = (value) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "yes") return "TRUE";
    if (normalized === "0" || normalized === "false" || normalized === "no") return "FALSE";
    return "-";
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-1">(Payment Calendar on v1) Batch Add Holiday: List</h1>
            <p className="text-gray-600">View and manage holidays added to payment calendar.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/batch-add-holiday-upload")}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faUpload} />
              Batch Add Holiday
            </button>
            <button
              onClick={handleOpenAddModal}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Holiday
            </button>
            <button
              onClick={fetchHolidayList}
              disabled={loading}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded-lg text-sm flex items-center gap-2"
              title="Refresh"
            >
              <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                onKeyDown={(event) => event.key === "Enter" && handleApplyFilters()}
                placeholder="Holiday name, added by, date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Added By</label>
              <input
                type="text"
                value={filters.addedBy}
                onChange={(event) => setFilters((prev) => ({ ...prev, addedBy: event.target.value }))}
                onKeyDown={(event) => event.key === "Enter" && handleApplyFilters()}
                placeholder="User name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Recurrent</label>
              <select
                value={filters.recurrent}
                onChange={(event) => setFilters((prev) => ({ ...prev, recurrent: event.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="true">TRUE</option>
                <option value="false">FALSE</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSearch} />
              Apply
            </button>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faTimes} />
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Holiday Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Holiday Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Added By</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : holidays.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                      No holiday records found.
                    </td>
                  </tr>
                ) : (
                  holidays.map((holiday) => (
                    <tr key={holiday.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800">{holiday.name || "-"}</td>
                      <td className="px-4 py-3 text-gray-800">{formatDateLong(holiday.date)}</td>
                      <td className="px-4 py-3 text-gray-700">{holiday.added_by || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewHoliday(holiday.id)}
                            className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium flex items-center gap-1.5"
                            title="View"
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View
                          </button>
                          <button
                            onClick={() => handleDeleteHoliday(holiday.id)}
                            disabled={deletingId === holiday.id}
                            className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded text-xs font-medium flex items-center gap-1.5"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            {deletingId === holiday.id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-600">
              Showing {holidays.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, pagination.total || 0)} of {pagination.total || 0} records
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Per page</span>
                <select
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1 || loading}
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <span className="text-sm font-medium text-gray-700">
                  Page {pagination.current_page || 1} of {pagination.total_pages || 1}
                </span>
                <button
                  onClick={() => setPage((prev) => Math.min(pagination.total_pages || 1, prev + 1))}
                  disabled={page >= (pagination.total_pages || 1) || loading}
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Add Holiday</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleAddHoliday} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name</label>
                <input
                  type="text"
                  value={addForm.holidayName}
                  onChange={(event) =>
                    setAddForm((prev) => ({ ...prev, holidayName: event.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Holiday Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={addForm.holidayDate}
                  onChange={(event) =>
                    setAddForm((prev) => ({ ...prev, holidayDate: event.target.value }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={addForm.recurrent}
                  onChange={(event) =>
                    setAddForm((prev) => ({ ...prev, recurrent: event.target.checked }))
                  }
                />
                Recurrent
              </label>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faCheck} />
                  {adding ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && viewingHoliday && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
                Holiday Details
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-5 space-y-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Holiday Name</p>
                <p className="text-gray-800 font-medium">{viewingHoliday.name || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Holiday Date</p>
                <p className="text-gray-800 font-medium">{formatDateLong(viewingHoliday.date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Added By</p>
                <p className="text-gray-800 font-medium">{viewingHoliday.added_by || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Recurrent</p>
                <p className="text-gray-800 font-medium">{formatRecurrent(viewingHoliday.recurrent)}</p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default BatchAddHolidayListPage;
